import { Component, Input, OnInit } from '@angular/core';

import { Yii } from "libraries/interfaces/Yii";
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { FormControl, FormGroup } from "@angular/forms";
import { IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { TicketingReportSearchAction } from "@afidium/ts-facade";
import {
    TicketSearchFormMapperService
} from "../ticketing-search-form-mapper.service";
import {
    IDateRangeOptionConfig,
    OptionConfigTypes
} from "app_sasha/shared/components/date-field-set/date-field-set.component";
import { actionTypeOptions } from "libraries/interfaces/action-type-options";
import { IDescriptionLabels } from "libraries/module/formbuilder/fields/form-field/components/field-description/field-description.component";

enum ETicketSearchFormActionFields {
    types = 'types',
    event = 'event',
    eventMin = 'eventMin',
    eventMax = 'eventMax',
    author = 'author',
}

export interface ITicketSearchFormActionOutput {
    [ETicketSearchFormActionFields.types]: string[];
    [ETicketSearchFormActionFields.event]?: {
        [ETicketSearchFormActionFields.eventMin]: Date;
        [ETicketSearchFormActionFields.eventMax]: Date;
    };
    [ETicketSearchFormActionFields.author]: string[];
}

interface IActionConfig {
    actionTypeConfig: IFieldSpecs;
    actionTypeOptions: IMultiselectOptions[];
    actionEventMinMaxConfig: IDateRangeOptionConfig;
    actionAuthorConfig: IFieldSpecs;
}

class Wording {
    public readonly actions: string = Yii.t('wui-sasha-js', 'Actions');
    public readonly [ETicketSearchFormActionFields.types]: string = Yii.t('wui-sasha-js', 'Types');
    public readonly [ETicketSearchFormActionFields.eventMin]: string = Yii.t('wui-sasha-js', 'Evènement min');
    public readonly [ETicketSearchFormActionFields.eventMax]: string = Yii.t('wui-sasha-js', 'Evènement max');
    public readonly [ETicketSearchFormActionFields.author]: string = Yii.t('wui-sasha-js', 'Auteur');
    public readonly typeDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour rechercher sur les types d\'actions effectuées sur les dossiers, parmi les différentes valeurs indiquées dans la liste.'),
    };
    public readonly dateElements: Partial<IDescriptionLabels> = {
        detailIntroduction: Yii.t('wui-sasha-js', 'Pour des recherches plus précises sur les dossiers aériens, utiliser les critères associés au vols. Ce champ supporte les expressions de date :'),
        detailList: [
            Yii.t('wui-sasha-js', 'aujourd\'hui : = ;'),
            Yii.t('wui-sasha-js', 'début du mois : < ;'),
            Yii.t('wui-sasha-js', 'fin du mois : > ;'),
            Yii.t('wui-sasha-js', 'dans deux semaines : +2w ;'),
            Yii.t('wui-sasha-js', 'dans trois mois : +3m .'),
        ],
    };
    public readonly eventMinDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour indiquer la date de départ minimale du voyage dans le dossier (qui peut être différente ce celle du billet) sur laquelle effectuer la recherche.'),
        ...this.dateElements,
    };
    public readonly eventMaxDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour indiquer la date de départ minimale du voyage dans le dossier (qui peut être différente ce celle du billet) sur laquelle effectuer la recherche.'),
        ...this.dateElements,
    };
    public readonly authorDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Champ de recherche sur les auteurs des actions effectuées sur les billets.'),
        detailIntroduction: Yii.t('wui-sasha-js', 'Ce champ cherche dans les dossiers les informations suivantes :'),
        detailList: [
            Yii.t('wui-sasha-js', 'noms d\'agents ;'),
            Yii.t('wui-sasha-js', 'références d\'agents ;'),
            Yii.t('wui-sasha-js', 'numéros de télephone d\'agents ;'),
            Yii.t('wui-sasha-js', 'adresses email d\'agents.'),
        ]
    };

}

@Component({
    selector: 'ticketing-action-fieldset',
    template: `
        <collapsible [title]="wording.actions" [open]="actionOpen">
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top">
                    <multiselect [apiOptions]="actionConfig.actionTypeOptions"
                                 [fieldConfig]="actionConfig.actionTypeConfig">
                    </multiselect>
                </div>

            </div>
            <div class="col-to-row-s-tb">
                <div class="flex-1">
                    <date-range-field [fieldConfig]="actionConfig.actionEventMinMaxConfig.fieldConfig"
                                      [formGroup]="myFormGroup.get(actionConfig.actionEventMinMaxConfig.formControl)"></date-range-field>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1">
                    <chips [fieldConfig]="actionConfig.actionAuthorConfig"></chips>
                </div>
            </div>
        </collapsible>
    `,
    styles: []
})
export class TicketingActionFieldsetComponent implements OnInit {

    @Input()
    public myFormGroup: FormGroup;

    @Input()
    public apiRequestAction: TicketingReportSearchAction;

    public readonly wording: Wording = new Wording();

    public actionOpen: boolean = true;
    public actionConfig: IActionConfig;

    public constructor(
        private ticketSearchFormMapperService: TicketSearchFormMapperService
    ) {
    }

    public ngOnInit(): void {
        this.createActionFormControls();
        this.createActionConfig();
        this.patchForm();
    }

    public patchForm(): void {
        if (!this.apiRequestAction) {
            this.myFormGroup.reset({});
        } else {
            this.myFormGroup.setValue(this.ticketSearchFormMapperService.mapApiActionToFormAction(this.apiRequestAction));
        }
    }

    public createActionFormControls(): void {
        this.myFormGroup.setControl(ETicketSearchFormActionFields.types, new FormControl());
        this.myFormGroup.setControl(ETicketSearchFormActionFields.event, new FormGroup({
            [ETicketSearchFormActionFields.eventMin]: new FormControl(),
            [ETicketSearchFormActionFields.eventMax]: new FormControl(),
        }));
        this.myFormGroup.setControl(ETicketSearchFormActionFields.author, new FormControl([]));
    }

    public createActionConfig(): void {
        this.actionConfig = {
            actionTypeConfig: {
                formGroup: this.myFormGroup,
                formControlName: ETicketSearchFormActionFields.types,
                label: this.wording[ETicketSearchFormActionFields.types],
                description: this.wording.typeDescription
            },
            actionTypeOptions,
            actionEventMinMaxConfig: {
                label: this.wording[ETicketSearchFormActionFields.eventMin],
                type: OptionConfigTypes.DateRange,
                fieldConfig: {
                    min: {
                        formGroup: this.myFormGroup.get(ETicketSearchFormActionFields.event) as FormGroup,
                        formControlName: ETicketSearchFormActionFields.eventMin,
                        maxDate: new Date(),
                        label: this.wording[ETicketSearchFormActionFields.eventMin],
                        description: this.wording.eventMinDescription,
                    },
                    max: {
                        formGroup: this.myFormGroup.get(ETicketSearchFormActionFields.event) as FormGroup,
                        formControlName: ETicketSearchFormActionFields.eventMax,
                        maxDate: new Date(),
                        label: this.wording[ETicketSearchFormActionFields.eventMax],
                        description: this.wording.eventMaxDescription
                    },
                },
                formControl: ETicketSearchFormActionFields.event,
            },
            actionAuthorConfig: {
                formGroup: this.myFormGroup,
                formControlName: ETicketSearchFormActionFields.author,
                label: this.wording[ETicketSearchFormActionFields.author],
                description: this.wording.authorDescription,
            },
        };
    }

}
